import { useEffect, useState, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, Users, User as UserIcon, Plus, X, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { getModuleLabel } from '@onvice/portal-ui';
import ModulePermissionsMatrix from '../../components/ModulePermissionsMatrix';

type Level = 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';

const LEVELS: Level[] = ['none', 'reader', 'tester', 'author', 'editor', 'admin'];

const LEVEL_COLOR: Record<Level, string> = {
  admin:    'bg-brand-500/20 text-brand-300 border-brand-500/30',
  editor:   'bg-sky-500/20 text-sky-300 border-sky-500/30',
  author:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tester:   'bg-purple-500/20 text-purple-300 border-purple-500/30',
  reader:   'bg-slate-500/20 text-slate-300 border-slate-500/30',
  depositor:'bg-amber-500/20 text-amber-300 border-amber-500/30',
  none:     'bg-slate-800 text-slate-500 border-slate-700',
};

interface ModuleDetail {
  module_slug: string;
  default_level: Level;
  persons: Array<{ subject_id: string; level: Level; first_name: string; last_name: string; email: string }>;
  groups: Array<{ subject_id: string; level: Level; name: string }>;
}

interface PersonOpt { id: string; first_name: string; last_name: string; email: string }
interface GroupOpt { id: string; name: string }

type PresetLevel = 'reader' | 'tester' | 'author' | 'editor' | 'admin';
type FeinKey = string; // "default" | "person:<id>" | "group:<id>"

/** Joerg 2026-09-08: modulspezifische Einstellungsseiten sind ueber die
 *  Modul-Verwaltung erreichbar. Sobald ein Modul eine eigene Settings-Route
 *  bekommt, hier den Eintrag ergaenzen — die Detailseite zeigt dann den
 *  „Modul-Einstellungen"-Link. */
const MODULE_SETTINGS_ROUTES: Record<string, string> = {
  // V2-Pfad — die V1-Settings-Seite (/m/bauberichte/settings) ist ueber die
  // Modul-ACL nicht mehr erreichbar (Fund Joerg 2026-09-08: Redirect auf /).
  bauberichte: '/m/bauberichteV2/einstellungen',
  bauberichteV2: '/m/bauberichteV2/einstellungen',
  // Joerg 2026-09-10: Wetterdienst-Schalter (Schlechtwetter-Anzeige).
  plantafel: '/m/plantafel/einstellungen',
};

export default function AdminAdressbuchModuleDetailPage() {
  const { slug } = useParams();
  const [detail, setDetail] = useState<ModuleDetail | null>(null);
  const [allPersons, setAllPersons] = useState<PersonOpt[]>([]);
  const [allGroups, setAllGroups] = useState<GroupOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Fein-ACL (Joerg 2026-08-20): pro (Modul, Subject) kann die Haekchen-Matrix
  // aufgeklappt werden — direkt unter der Level-Zeile erscheint der Editor.
  const [feinOpen, setFeinOpen] = useState<Set<FeinKey>>(new Set());
  const toggleFein = (key: FeinKey) => setFeinOpen((prev) => {
    const s = new Set(prev);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const isPresetLevel = (l: Level): l is PresetLevel =>
    l === 'reader' || l === 'tester' || l === 'author' || l === 'editor' || l === 'admin';

  function load() {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: ModuleDetail }>(`/admin/adressbuch/modules/${slug}`),
      api.get<{ success: boolean; data: PersonOpt[] }>(`/admin/adressbuch/persons`),
      api.get<{ success: boolean; data: GroupOpt[] }>(`/admin/adressbuch/groups`),
    ])
      .then(([m, p, g]) => { setDetail(m.data); setAllPersons(p.data); setAllGroups(g.data); })
      .catch((err) => setError(err.message ?? 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [slug]);

  async function setLevel(subjectType: 'person' | 'group', subjectId: string, level: Level) {
    if (!slug) return;
    setBusy(true);
    try {
      await api.put(`/admin/adressbuch/modules/${slug}/access/${subjectType}/${subjectId}`, { level });
      load();
    } finally {
      setBusy(false);
    }
  }
  async function setDefaultLevel(level: Level) {
    if (!slug) return;
    setBusy(true);
    try {
      await api.put(`/admin/adressbuch/modules/${slug}/default`, { level });
      load();
    } finally {
      setBusy(false);
    }
  }

  if (loading && !detail) return <div className="px-4 sm:px-8 py-6 text-slate-400">Laden…</div>;
  if (error) return <div className="px-4 sm:px-8 py-6 text-red-400">{error}</div>;
  if (!detail) return null;

  const personsInAcl = new Set(detail.persons.map((p) => p.subject_id));
  const groupsInAcl = new Set(detail.groups.map((g) => g.subject_id));
  const personsToAdd = allPersons.filter((p) => !personsInAcl.has(p.id));
  const groupsToAdd = allGroups.filter((g) => !groupsInAcl.has(g.id));

  return (
    <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/adressbuch" className="text-slate-400 hover:text-slate-200 -ml-2 p-2">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Shield size={20} />
          <span>{getModuleLabel(detail.module_slug)}</span>
          <span className="font-mono text-sm text-muted-foreground">{detail.module_slug}</span>
        </h1>
      </div>

      {/* Modul-Einstellungen (Joerg 2026-09-08): Einstieg zur fachlichen
          Einstellungsseite des Moduls, wenn es eine hat. */}
      {MODULE_SETTINGS_ROUTES[detail.module_slug] && (
        <section className="bg-card border border-default rounded-lg p-4">
          <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
            <Settings size={15} /> Modul-Einstellungen
          </h2>
          <p className="text-[11px] text-slate-500 mb-3">
            Fachliche Optionen dieses Moduls (unabhängig von den Zugriffsrechten unten).
          </p>
          <Link
            to={MODULE_SETTINGS_ROUTES[detail.module_slug]!}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm min-h-[40px]"
          >
            <Settings size={15} /> Einstellungen öffnen
          </Link>
        </section>
      )}

      {/* Default */}
      <section className="bg-card border border-default rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Default</h2>
        <p className="text-[11px] text-slate-500 mb-3">
          Greift für alle Personen, die weder direkt noch über eine Gruppe einen Eintrag haben.
        </p>
        <div className="flex items-center gap-2">
          <LevelDropdown value={detail.default_level} disabled={busy} onChange={(l) => setDefaultLevel(l)} />
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${LEVEL_COLOR[detail.default_level]}`}>
            {detail.default_level}
          </span>
          <FeinToggleButton open={feinOpen.has('default')} onClick={() => toggleFein('default')} />
        </div>
        {feinOpen.has('default') && (
          <div className="mt-3">
            <ModulePermissionsMatrix
              moduleSlug={detail.module_slug}
              subjectType="default"
              subjectId={null}
              currentLevel={isPresetLevel(detail.default_level) ? detail.default_level : undefined}
              onChanged={load}
            />
          </div>
        )}
      </section>

      {/* Gruppen */}
      <section className="bg-card border border-default rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Users size={14} /> Gruppen mit Eintrag
        </h2>
        {detail.groups.length === 0 ? (
          <p className="text-xs text-slate-500 italic mb-3">Keine Gruppen-Einträge</p>
        ) : (
          <div className="space-y-1 mb-3">
            {detail.groups.map((g) => {
              const key = `group:${g.subject_id}`;
              const open = feinOpen.has(key);
              return (
                <Fragment key={g.subject_id}>
                  <div className="flex items-center gap-2 px-3 py-2 rounded border border-default">
                    <span className="text-sm flex-1">{g.name}</span>
                    <LevelDropdown value={g.level} disabled={busy} onChange={(l) => setLevel('group', g.subject_id, l)} />
                    <FeinToggleButton open={open} onClick={() => toggleFein(key)} />
                    <button
                      onClick={() => setLevel('group', g.subject_id, 'none')}
                      disabled={busy}
                      title="Eintrag entfernen"
                      className="p-1 text-slate-500 hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {open && (
                    <div className="px-3">
                      <ModulePermissionsMatrix
                        moduleSlug={detail.module_slug}
                        subjectType="group"
                        subjectId={g.subject_id}
                        currentLevel={isPresetLevel(g.level) ? g.level : undefined}
                        onChanged={load}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
        {groupsToAdd.length > 0 && (
          <AddRow
            label="Gruppe hinzufügen"
            options={groupsToAdd.map((g) => ({ value: g.id, label: g.name }))}
            onAdd={(id, level) => setLevel('group', id, level)}
            disabled={busy}
          />
        )}
      </section>

      {/* Personen */}
      <section className="bg-card border border-default rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <UserIcon size={14} /> Personen mit Direct-Eintrag
        </h2>
        {detail.persons.length === 0 ? (
          <p className="text-xs text-slate-500 italic mb-3">Keine Direct-Person-Einträge</p>
        ) : (
          <div className="space-y-1 mb-3">
            {detail.persons.map((p) => {
              const key = `person:${p.subject_id}`;
              const open = feinOpen.has(key);
              return (
                <Fragment key={p.subject_id}>
                  <div className="flex items-center gap-2 px-3 py-2 rounded border border-default">
                    <span className="text-sm flex-1">
                      <Link to={`/admin/adressbuch/persons/${p.subject_id}`} className="hover:text-brand-400">
                        {p.first_name} {p.last_name}
                      </Link>
                      <span className="text-[11px] text-slate-500 ml-2">{p.email}</span>
                    </span>
                    <LevelDropdown value={p.level} disabled={busy} onChange={(l) => setLevel('person', p.subject_id, l)} />
                    <FeinToggleButton open={open} onClick={() => toggleFein(key)} />
                    <button
                      onClick={() => setLevel('person', p.subject_id, 'none')}
                      disabled={busy}
                      title="Eintrag entfernen"
                      className="p-1 text-slate-500 hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {open && (
                    <div className="px-3">
                      <ModulePermissionsMatrix
                        moduleSlug={detail.module_slug}
                        subjectType="person"
                        subjectId={p.subject_id}
                        currentLevel={isPresetLevel(p.level) ? p.level : undefined}
                        onChanged={load}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
        {personsToAdd.length > 0 && (
          <AddRow
            label="Person hinzufügen"
            options={personsToAdd.map((p) => ({ value: p.id, label: `${p.first_name} ${p.last_name} (${p.email})` }))}
            onAdd={(id, level) => setLevel('person', id, level)}
            disabled={busy}
          />
        )}
      </section>
    </div>
  );
}

function FeinToggleButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Fein-Rechte-Matrix ein-/ausklappen"
      className={`flex items-center gap-1 rounded border px-2 h-8 text-[11px] transition ${
        open
          ? 'border-brand-500 bg-brand-500/20 text-brand-200'
          : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500'
      }`}
    >
      {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      Rechte
    </button>
  );
}

function LevelDropdown({ value, onChange, disabled }: { value: Level; onChange: (l: Level) => void; disabled?: boolean }) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Level)}
      className="h-8 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-[12px]"
    >
      {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  );
}

function AddRow({ label, options, onAdd, disabled }: {
  label: string;
  options: Array<{ value: string; label: string }>;
  onAdd: (id: string, level: Level) => void;
  disabled?: boolean;
}) {
  const [sel, setSel] = useState<string>('');
  const [level, setLevel] = useState<Level>('reader');
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded border border-dashed border-default">
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        disabled={disabled}
        className="flex-1 h-8 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-[12px]"
      >
        <option value="">{label}…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <LevelDropdown value={level} disabled={disabled} onChange={setLevel} />
      <button
        onClick={() => { if (sel) { onAdd(sel, level); setSel(''); } }}
        disabled={disabled || !sel}
        className="flex items-center gap-1 px-2 py-1 rounded bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-[12px]"
      >
        <Plus size={12} /> Hinzufügen
      </button>
    </div>
  );
}
